import {
  docKind,
  daysInMonth,
  monthNames,
  parsePeriod,
  shortDate,
  officialKindsUsed,
  officialRoleLine,
  officialsOf,
  shortName,
  type IsoDate,
  type Person,
} from '@kancelaria/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorText } from '../../api/client.js';
import { useData } from '../../store/data.js';
import { toast } from '../../store/ui.js';
import { Field, Modal } from '../ui.js';
import { PersonSelect } from '../PersonSelect.js';

/** Рапорт на компенсацію: вибір людини, періоду й підписантів. */
export function ReportModal({
  person,
  from,
  to,
  onClose,
}: {
  person: Person | null;
  from: IsoDate;
  to: IsoDate;
  onClose: () => void;
}): ReactNode {
  const { people, officials, templates, session } = useData();
  const settings = session!.org.settings;
  const active = useMemo(() => people.filter(p => p.active), [people]);

  const [personId, setPersonId] = useState(person?.id ?? active[0]?.id ?? '');
  const [period, setPeriod] = useState({ from, to });
  // Період набирається коротко — «01-15». Місяць для таких чисел обирають окремо.
  const [anchor, setAnchor] = useState(() => ({
    year: Number(from.slice(0, 4)),
    month0: Number(from.slice(5, 7)) - 1,
  }));
  const asText = (a: IsoDate, b: IsoDate): string =>
    a.slice(0, 7) === b.slice(0, 7)
      ? `${a.slice(8)}-${b.slice(8)}`
      : `${shortDate(a)}-${shortDate(b)}`;
  const [periodText, setPeriodText] = useState(() => asText(from, to));
  const parsed = parsePeriod(periodText, anchor.year, anchor.month0);
  const monthKey = `${anchor.year}-${String(anchor.month0 + 1).padStart(2, '0')}`;
  const names = monthNames(settings.locale);

  // Період могли змінити з розбору «09.08-17.08» — підтягуємо місяць під нього.
  useEffect(() => {
    setAnchor({
      year: Number(period.from.slice(0, 4)),
      month0: Number(period.from.slice(5, 7)) - 1,
    });
  }, [period.from]);

  const monthOptions = useMemo(() => {
    const out: string[] = [];
    for (let y = anchor.year - 1; y <= anchor.year + 1; y++) {
      for (let m = 1; m <= 12; m++) out.push(`${y}-${String(m).padStart(2, '0')}`);
    }
    if (!out.includes(monthKey)) out.unshift(monthKey);
    return out;
  }, [anchor.year, monthKey]);

  const applyMonth = (ym: string): void => {
    const y = Number(ym.slice(0, 4));
    const m0 = Number(ym.slice(5, 7)) - 1;
    setAnchor({ year: y, month0: m0 });
    const r = parsePeriod(periodText, y, m0);
    if (r) {
      setPeriod(r);
      return;
    }
    const last = daysInMonth(y, m0);
    const clamp = (d: IsoDate): IsoDate => {
      const day = Math.min(Number(d.slice(8)), last);
      return `${ym}-${String(day).padStart(2, '0')}` as IsoDate;
    };
    setPeriod({ from: clamp(period.from), to: clamp(period.to) });
  };
  const reportTemplates = useMemo(() => templates.filter(t => t.kind === 'report'), [templates]);
  const [templateId, setTemplateId] = useState(
    settings.documents.defaultTemplates.report ?? reportTemplates[0]?.id ?? '',
  );
  const [chosen, setChosen] = useState<Record<string, string>>(
    () => reportTemplates.find(t => t.id === templateId)?.defaults ?? {},
  );
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  // Підписанти й бланк уже задані в налаштуваннях і в самому бланку. Показуємо
  // їх лише на вимогу: щоденний шлях — набрати період і завантажити.
  const [showSigners, setShowSigners] = useState(false);

  const kinds = useMemo(
    () => docKind('report')?.signedBy ?? officialKindsUsed(officials),
    [officials],
  );
  const template = reportTemplates.find(t => t.id === templateId);

  // Бланк може бути «на конкретного начальника» — тоді підставляємо його підписантів.
  useEffect(() => {
    setChosen(template?.defaults ?? {});
  }, [templateId]);

  useEffect(() => {
    if (!personId) return;
    let alive = true;
    api
      .post<{ text: string }>('/api/documents/report', {
        personId,
        from: period.from,
        to: period.to,
        officials: chosen,
        preview: true,
      })
      .then(r => alive && setPreview(r.text))
      .catch(e => alive && setPreview(errorText(e)));
    return () => {
      alive = false;
    };
  }, [personId, period, chosen]);

  const download = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.download('/api/documents/report', `Р+_Харчування_${period.from}_${period.to}.docx`, {
        personId,
        from: period.from,
        to: period.to,
        officials: chosen,
        templateId: templateId || undefined,
      });
      toast('Рапорт сформовано', 'ok');
    } catch (e) {
      toast(errorText(e), 'err');
    } finally {
      setBusy(false);
    }
  };

  const kindLabel: Record<string, string> = {
    chief: 'Кому (начальник)',
    deputy: 'Заступник',
    courseHead: 'Клопоче',
    medic: 'Погоджує',
  };

  return (
    <Modal
      title="Рапорт"
      onClose={onClose}
      width={780}
      footer={
        <>
          <span className="muted" style={{ marginRight: 'auto', fontSize: 12 }}>
            {template ? `Бланк: ${template.name}` : 'Розкладка з налаштувань'}
          </span>
          <button className="btn primary" disabled={busy || !personId} onClick={() => void download()}>
            {busy ? 'Формую…' : 'Завантажити .docx'}
          </button>
        </>
      }
    >
      <div className="field-row">
        <Field label="Військовослужбовець">
          <PersonSelect
            style={{ minWidth: 240 }}
            value={personId}
            onChange={setPersonId}
            options={active.map(p => ({ id: p.id, label: shortName(p) }))}
          />
        </Field>
        <Field label="Період">
          <div className="field-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <select
              className="select"
              style={{ width: 160 }}
              value={monthKey}
              title="Місяць для чисел без дати"
              onChange={e => applyMonth(e.target.value)}
            >
              {monthOptions.map(ym => {
                const y = Number(ym.slice(0, 4));
                const m = Number(ym.slice(5, 7)) - 1;
                return (
                  <option key={ym} value={ym}>
                    {names[m]} {y}
                  </option>
                );
              })}
            </select>
            <input
              className="input"
              style={{ width: 120 }}
              value={periodText}
              placeholder="01-15"
              onChange={e => {
                setPeriodText(e.target.value);
                const r = parsePeriod(e.target.value, anchor.year, anchor.month0);
                // Поки набирають, період лишається попереднім: перегляд не
                // має блимати помилкою після кожної натиснутої цифри.
                if (r) setPeriod(r);
              }}
            />
          </div>
          <div className="hint-line">
            {parsed
              ? `${shortDate(parsed.from)} – ${shortDate(parsed.to)}`
              : 'не зрозумів: 01-15, 09.08-17.08 або 16'}
          </div>
        </Field>
      </div>

      <div className="btn-row" style={{ marginBottom: 8 }}>
        <button className="btn sm" onClick={() => setShowSigners(v => !v)}>
          {showSigners ? 'Сховати підписантів' : 'Підписанти та бланк'}
        </button>
      </div>

      <div
        className="auto-grid"
        style={{ ['--min' as string]: '200px', display: showSigners ? undefined : 'none' }}
      >
        {kinds.map(kind => {
          const list = officialsOf(officials, kind);
          if (!list.length) return null;
          return (
            <Field key={kind} label={kindLabel[kind] ?? kind}>
              <select
                className="select"
                value={chosen[kind] ?? list[0]!.id}
                onChange={e => setChosen({ ...chosen, [kind]: e.target.value })}
              >
                {list.map(o => (
                  <option key={o.id} value={o.id}>
                    {officialRoleLine(o, kind)}
                  </option>
                ))}
              </select>
            </Field>
          );
        })}
        {reportTemplates.length ? (
          <Field label="Бланк">
            <select className="select" value={templateId} onChange={e => setTemplateId(e.target.value)}>
              {reportTemplates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>

      <Field label="Попередній перегляд">
        <textarea className="textarea" style={{ height: 260 }} readOnly value={preview} />
      </Field>
      {!settings.report.body.includes('{MEALS}') ? (
        <p className="error-text">
          У тексті рапорту немає підстановки {'{MEALS}'} — перевірте налаштування документів.
        </p>
      ) : null}
    </Modal>
  );
}
